// Stav sítě na tabletu (F4 4.5): „bez signálu“ pozná aplikace ze dvou zdrojů — z prohlížeče
// (`navigator.onLine`, události online/offline) a z toho, jak dopadl poslední požadavek (`sit.nedostupna`,
// HTTP status 0). Prohlížeč umí tvrdit, že online je, i když je v autě signál jen naoko; proto platí:
// jakmile požadavek spadne na síti, bereme se jako offline, dokud něco neprojde.
//
// Wifi × mobilní data (rozhodnutí 15. 9. 2026): **zmenšená fotka odchází hned i na datech**, originál se
// pošle, až je wifi. Typ připojení hlásí Network Information API (Chrome, Android — tablet firmy); kde ho
// prohlížeč nemá, bereme připojení jako wifi (jinak by originály nedorazily nikdy). Zapnutý „spořič dat“
// znamená mobilní data i tehdy, když typ neznáme.
export type PosluchacSite = (offline: boolean) => void;

interface SitovaInformace {
  type?: string;
  effectiveType?: string;
  saveData?: boolean;
  addEventListener?: (typ: string, posluchac: () => void) => void;
  removeEventListener?: (typ: string, posluchac: () => void) => void;
}

function pripojeni(): SitovaInformace | undefined {
  const n =
    typeof navigator === 'undefined'
      ? undefined
      : (navigator as Navigator & { connection?: SitovaInformace });
  return n?.connection;
}

/** Poslední požadavek spadl na síti (i když prohlížeč tvrdí, že online je). */
let sitSelhala = false;
const posluchaci = new Set<PosluchacSite>();

function oznam(): void {
  const stav = jeOffline();
  for (const p of posluchaci) p(stav);
}

/** Aplikace se bere jako bez signálu. */
export function jeOffline(): boolean {
  const prohlizec = typeof navigator !== 'undefined' && navigator.onLine === false;
  return prohlizec || sitSelhala;
}

/** Požadavek spadl na síti — od téhle chvíle jedeme offline, dokud něco neprojde. */
export function hlasSelhaniSite(): void {
  if (sitSelhala) return;
  sitSelhala = true;
  oznam();
}

/** Požadavek prošel — signál je zpátky. */
export function hlasUspechSite(): void {
  if (!sitSelhala) return;
  sitSelhala = false;
  oznam();
}

/** Přihlásí posluchače změn stavu sítě; vrací odhlášení. */
export function sledujSit(posluchac: PosluchacSite): () => void {
  posluchaci.add(posluchac);
  const zmena = (): void => {
    if (typeof navigator !== 'undefined' && navigator.onLine) sitSelhala = false;
    oznam();
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('online', zmena);
    window.addEventListener('offline', zmena);
  }
  const spojeni = pripojeni();
  spojeni?.addEventListener?.('change', zmena);
  return () => {
    posluchaci.delete(posluchac);
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', zmena);
      window.removeEventListener('offline', zmena);
    }
    spojeni?.removeEventListener?.('change', zmena);
  };
}

/** Je tablet na wifi? (originály fotek čekají na wifi — rozhodnutí 15. 9. 2026) */
export function jeWifi(): boolean {
  if (jeOffline()) return false;
  const spojeni = pripojeni();
  if (!spojeni) return true; // prohlížeč typ připojení nehlásí — bereme jako wifi
  if (spojeni.saveData === true) return false;
  const typ = spojeni.type;
  if (typ) return typ === 'wifi' || typ === 'ethernet' || typ === 'unknown';
  return false;
}

/** Jen pro testy: vynuluje zapamatovaný stav sítě. */
export function vynulujStavSite(): void {
  sitSelhala = false;
  posluchaci.clear();
}
